'use client'

import Skeleton from "@/components/elements/Skeleton"
import EmptyState from "../elements/EmptyState"
import CenterModal from "../elements/CenterModal"
import { panelFocusRing } from "../elements/SidePanel"
import { SelectField, TextField, UnderlineTabs } from "../elements/Field"
import { MapPinHouse } from "lucide-react"
import { useState } from "react"
import { toast } from "@/lib/toast"
import AddressEdit from "./AddressEdit"
import { formatAddressDetails } from "@/utils/order"
import { useAddressForm, useAddressManagement } from "@/lib/commerce/hooks"

const TYPE_LABELS: Record<string, string> = { apartment: 'Орон сууц', house: 'Хашаа байшин', office: 'Оффис' }
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
export const solidButton = `h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`
const outlinedButton = `h-11 px-6 border border-foreground text-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background ${panelFocusRing}`
const textAction = `text-[13px] pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[0%_1px] hover:bg-size-[100%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed ${panelFocusRing}`

export default function AddressList({ addressList, addressField, vendor }: { addressList: any, addressField: any, vendor: any }) {
  const address = addressList || []
  const [isLoading, setIsLoading] = useState(addressList === undefined || addressField === undefined)
  if (isLoading && addressList !== undefined && addressField !== undefined) setIsLoading(false)

  const form = useAddressForm({ addressField, vendor })
  const mgmt = useAddressManagement()

  const handleAdd = async () => {
    const v = form.validate()
    if (!v.ok) return toast.error(v.error)
    const ok = await mgmt.addAddress(form.compose())
    if (ok) {
      mgmt.setOpenAdd(false)
      form.reset()
    }
  }

  if (isLoading) return <AddressListSkeleton />

  return (
    <>
      <div>
        <div className="flex items-end justify-between gap-4 pb-6 border-b border-border">
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl tracking-tight text-foreground">Хаягууд</h1>
            <p className="mt-2 text-sm text-muted-foreground">Таны хүргэлт авах байршил хаягууд</p>
          </div>
          {address.length > 0 && (
            <button type="button" onClick={() => mgmt.setOpenAdd(true)} className={`shrink-0 ${outlinedButton}`}>Хаяг нэмэх</button>
          )}
        </div>

        {address.length > 0 ? (
          <ul className="divide-y divide-border">
            {address.map((a: any) => {
              const isDefaulting = mgmt.defaultingId === a.id
              const isDeleting = mgmt.deletingId === a.id
              const rowBusy = isDefaulting || isDeleting
              return (
                <li key={a.id} className={`py-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 transition-opacity duration-150 ${rowBusy ? 'opacity-60' : ''}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={eyebrow}>{TYPE_LABELS[a.type || 'apartment'] || TYPE_LABELS.apartment}</span>
                      {a.isDefault && <span className="inline-flex items-center h-5 px-2 border border-foreground text-[10px] tracking-wide">Үндсэн</span>}
                    </div>
                    <p className="mt-2 text-sm text-foreground">{`${a?.city}, ${a?.duureg}, ${a?.khoroo}, ${formatAddressDetails(a)}`}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{a?.additionalAddress ? a.additionalAddress : 'Нэмэлт мэдээлэл байхгүй'}</p>
                  </div>
                  <div className="flex items-center gap-5 shrink-0">
                    {!a.isDefault && (
                      <button type="button" disabled={rowBusy} onClick={() => mgmt.setDefault(a.id)} className={textAction}>
                        {isDefaulting ? 'Хадгалж байна…' : 'Үндсэн болгох'}
                      </button>
                    )}
                    <button type="button" disabled={rowBusy} onClick={() => mgmt.startEdit(a)} className={textAction}>Засах</button>
                    <button type="button" disabled={rowBusy} onClick={() => mgmt.removeAddress(a.id)} className={`text-muted-foreground hover:text-foreground ${textAction}`}>
                      {isDeleting ? 'Устгаж байна…' : 'Устгах'}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        ) : (
          <EmptyState icon={MapPinHouse} title="Хаяг байхгүй байна" description="Таны нэмсэн шинэ хаягууд энд харагдах болно.">
            <button type="button" onClick={() => mgmt.setOpenAdd(true)} className={outlinedButton}>Хаяг нэмэх</button>
          </EmptyState>
        )}
      </div>

      <CenterModal
        open={mgmt.openAdd}
        onClose={() => mgmt.setOpenAdd(false)}
        title="Шинэ хаяг"
        footer={
          <div className="flex justify-end">
            <button type="button" onClick={handleAdd} disabled={mgmt.submitting} className={solidButton}>{mgmt.submitting ? 'Хадгалж байна…' : 'Хадгалах'}</button>
          </div>
        }
      >
        <AddressFormFields form={form} />
      </CenterModal>

      <AddressEdit open={mgmt.openEdit} setOpen={mgmt.setOpenEdit} address={mgmt.addressToEdit} addressField={addressField} vendor={vendor} updateAddress={mgmt.updateAddress} submitting={mgmt.submitting} />
    </>
  )
}

export function AddressFormFields({ form }: { form: ReturnType<typeof useAddressForm>, vendor?: any }) {
  return (
    <div className="flex flex-col gap-5">
      <UnderlineTabs label="Хаягийн төрөл" options={[{ value: 'apartment', label: 'Орон сууц' }, { value: 'house', label: 'Хашаа байшин' }, { value: 'office', label: 'Оффис' }]} value={form.addressType} onChange={value => form.setAddressType(value as any)} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <SelectField label="Хот / Аймаг" items={form.provinces} value={form.selectedProvince || ''} onChange={form.onProvinceChange} required />
        <SelectField label="Дүүрэг / Сум" items={form.districts} value={form.selectedDistrict || ''} onChange={form.onDistrictChange} required />
        <SelectField label="Хороо / Баг" items={form.subdistricts} value={form.selectedSubdistrict || ''} onChange={form.onSubdistrictChange} required />
      </div>
      {form.addressType === 'apartment' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Байр" placeholder="Байрны дугаар" value={form.bair} onChange={e => form.setBair(e.target.value)} required />
          <TextField label="Тоот" placeholder="Хаалганы тоот" value={form.toot} onChange={e => form.setToot(e.target.value)} required />
        </div>
      )}
      {form.addressType === 'house' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Хашааны дугаар" placeholder="Хашааны дугаар" value={form.houseNum} onChange={e => form.setHouseNum(e.target.value)} required />
          <TextField label="Гудамж" placeholder="Гудамжны нэр" value={form.street} onChange={e => form.setStreet(e.target.value)} />
        </div>
      )}
      {form.addressType === 'office' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <TextField label="Барилга" placeholder="Барилгын нэр/дугаар" value={form.building} onChange={e => form.setBuilding(e.target.value)} required />
          <TextField label="Давхар" placeholder="Давхар" value={form.floor} onChange={e => form.setFloor(e.target.value)} required />
          <TextField label="Тоот" placeholder="Хаалганы тоот" value={form.toot} onChange={e => form.setToot(e.target.value)} required />
          <TextField label="Оффисын нэр" placeholder="Заавал биш" value={form.officeName} onChange={e => form.setOfficeName(e.target.value)} />
        </div>
      )}
      <TextField label="Нэмэлт мэдээлэл" placeholder="Орц, давхар, орцны код гэх мэт" value={form.additionalAddress} onChange={e => form.setAdditionalAddress(e.target.value)} />
    </div>
  )
}

function AddressListSkeleton() {
  return (
    <div>
      <div className="flex items-end justify-between gap-4 pb-6 border-b border-border">
        <div className="space-y-2">
          <Skeleton className="w-32 h-8 rounded-none" />
          <Skeleton className="w-64 h-4 rounded-none" />
        </div>
        <Skeleton className="w-32 h-11 rounded-none" />
      </div>
      <div className="divide-y divide-border">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="py-6 space-y-2">
            <Skeleton className="w-20 h-3 rounded-none" />
            <Skeleton className="w-80 max-w-full h-4 rounded-none" />
            <Skeleton className="w-48 h-3 rounded-none" />
          </div>
        ))}
      </div>
    </div>
  )
}
